import { inject, injectable } from '../../modules/common/di';
import { BaseService } from './baseService';
import { AuthService } from './authService';
import { BillingApi } from '../api/admin/billing/billingApi';
import { GetOrganizationBillsApiResponse } from '../api/admin/billing/getOrganizationBillsApiResponse';
import { GenerateOrganizationBillApiResponse } from '../api/admin/billing/generateOrganizationBillApiResponse';
import { GetOrganizationBillingPlansApiResponse } from '../api/admin/billing/getOrganizationBillingPlansApiResponse';
import { ApiResponseBase } from '../models/apiResponseBase';
import { GetBillingItemsApiResponse } from '../api/admin/billing/getBillingItemsApiResponse';
import { GetBillingPlansApiResponse } from '../api/admin/billing/getBillingPlansApiResponse';
import { UpdateBillingPlanApiResponse, UpdateBillingPlanModel } from '../api/admin/billing/updateBillingPlanApiResponse';
import { GetBillingPlanVersionsApiResponse } from '../api/admin/billing/getBillingPlanVersionsApiResponse';
import { BillingPlanVersionModel, UpdateBillingPlanVersionApiResponse } from '../api/admin/billing/updateBillingPlanVersionApiResponse';

/**
 * Exposes interface to manage bills.
 */
@injectable('BillingService')
export class BillingService extends BaseService {
  @inject('BillingApi') protected readonly billingApi!: BillingApi;
  @inject('AuthService') private readonly authService!: AuthService;

  constructor() {
    super();
  }

  // #region Billing Plans

  /**
   * Returns all billing items.
   * @returns {Promise<GetBillingItemsApiResponse>}
   */
  public getBillingItems():
    Promise<GetBillingItemsApiResponse> {
      return this.authService
        .ensureAuthenticated()
        .then(() => this.billingApi.getBillingItems());
  }

  /**
   * Returns available billing plans.
   * @returns {Promise<GetBillingPlansApiResponse>}
   */
  public getBillingPlans():
    Promise<GetBillingPlansApiResponse> {
      return this.authService
        .ensureAuthenticated()
        .then(() => this.billingApi.getBillingPlans());
  }

  /**
   * Create or update existing billing plan.
   * @param {number} [planId] Billing plan ID to update.
   * @returns {Promise<UpdateBillingPlanApiResponse>}
   */
  public setBillingPlan(billingPlan: UpdateBillingPlanModel, planId?: number):
    Promise<UpdateBillingPlanApiResponse> {
      const params: {
        planId?: number;
      } = {
        planId: planId,
      };

      return this.authService
        .ensureAuthenticated()
        .then(() => this.billingApi.setBillingPlan(billingPlan, params));
  }

  /**
   * Get billing plan versions.
   * @param {number} planId Plan ID.
   * @returns {Promise<GetBillingPlanVersionsApiResponse>}
   */
  public getBillingPlanVersions(planId: number):
    Promise<GetBillingPlanVersionsApiResponse> {
      if (planId < 1 || isNaN(planId)) {
        return this.reject(`Plan ID is incorrect [${planId}].`);
      }

      return this.authService
        .ensureAuthenticated()
        .then(() => this.billingApi.getBillingPlanVersions(planId));
  }

  /**
   * Create or update billing plan version.
   * @param {number} planId Plan ID.
   * @param {BillingPlanVersionModel} billingPlanVersion
   * @param params Requested params
   * @param {number} [params.planVersionId] Plan version ID to update.
   * @returns {Promise<UpdateBillingPlanVersionApiResponse>}
   */
  public setBillingPlanVersion(planId: number, billingPlanVersion: BillingPlanVersionModel, params?: {
    planVersionId?: number,
  }):
    Promise<UpdateBillingPlanVersionApiResponse> {
      if (planId < 1 || isNaN(planId)) {
        return this.reject(`Plan ID is incorrect [${planId}].`);
      }

      return this.authService
        .ensureAuthenticated()
        .then(() => this.billingApi.setBillingPlanVersion(planId, billingPlanVersion, params));
  }

  // #endregion

  // #region Organization Billing Plans

  /**
   * Retrieve all organization billing plans.
   * @param {number} organizationId Organization ID.
   * @returns {Promise<GetOrganizationBillingPlansApiResponse>}
   */
  public getOrganizationBillingPlans(organizationId: number, params?: {
    startDate?: string|number,
    endDate?: string|number,
  }):
    Promise<GetOrganizationBillingPlansApiResponse> {
      if (organizationId < 1 || isNaN(organizationId)) {
        return this.reject(`Organization ID is incorrect [${organizationId}].`);
      }

      return this.authService
        .ensureAuthenticated()
        .then(() => this.billingApi.getOrganizationBillingPlans(organizationId, params));
  }

  /**
   * Set new billing plan for the organization.
   * @param {number} organizationId Organization ID.
   * @param params Requested params
   * @param {number} params.planId New billing plan ID.
   * @param {string|number} [params.startDate] New plan start date.
   * @returns {Promise<ApiResponseBase>}
   */
  public setOrganizationBillingPlan(organizationId: number, params: {
    planId: number,
    startDate?: string|number,
  }):
    Promise<ApiResponseBase> {
      if (organizationId < 1 || isNaN(organizationId)) {
        return this.reject(`Organization ID is incorrect [${organizationId}].`);
      }
      if (!params || !params.planId || isNaN(params.planId)) {
        return this.reject(`Plan ID need to be set.`);
      }

      return this.authService
        .ensureAuthenticated()
        .then(() => this.billingApi.setOrganizationBillingPlan(organizationId, params));
  }

  /**
   * Delete or schedule deletion of the current organization billing plan.
   * @param {number} organizationId Organization ID.
   * @param {string|number} [startDate] Stop plan billing date.
   * @returns {Promise<ApiResponseBase>}
   */
  public deleteOrganizationBillingPlan(organizationId: number, startDate?: string|number):
    Promise<ApiResponseBase> {
      if (organizationId < 1 || isNaN(organizationId)) {
        return this.reject(`Organization ID is incorrect [${organizationId}].`);
      }

      const params: {
        startDate?: string|number;
      } = {
        startDate: startDate,
      };

      return this.authService
        .ensureAuthenticated()
        .then(() => this.billingApi.deleteOrganizationBillingPlan(organizationId, params));
  }

  // #endregion

  // #region Organization Billing

  /**
   * Shows the list of bills generated for the organization for specified date range.
   * @param {number} organizationId Organization ID.
   * @param params Requested params
   * @param {string|number} params.startDate Start date.
   * @param {string|number} [params.endDate] End date, default is 1st day of the current month.
   * @returns {Promise<GetOrganizationBillsApiResponse>}
   */
  public getOrganizationBills(organizationId: number, params: {
    startDate: string|number,
    endDate?: string|number,
  }):
    Promise<GetOrganizationBillsApiResponse> {
      if (organizationId < 1 || isNaN(organizationId)) {
        return this.reject(`Organization ID is incorrect [${organizationId}].`);
      }
      if (!params || !params.startDate) {
        return this.reject(`Start date need to be set.`);
      }

      return this.authService
        .ensureAuthenticated()
        .then(() => this.billingApi.getOrganizationBills(organizationId, params));
  }

  /**
   * Generates a bill for one billing period (month).
   * The generated bill is saved in the database and displayed in the response body.
   * The response body also displays the input data used to calculate the bill.
   * @param {number} organizationId Organization ID.
   * @param {string|number} date The date following the end of the target billing period. By default, the current date is used.
   * @returns {Promise<GenerateOrganizationBillApiResponse>}
   */
  public generateOranizationBill(organizationId: number, date?: string|number):
    Promise<GenerateOrganizationBillApiResponse> {
      if (organizationId < 1 || isNaN(organizationId)) {
        return this.reject(`Organization ID is incorrect [${organizationId}].`);
      }

      const params: {
        date?: string|number;
      } = {
        date: date,
      };

      return this.authService
        .ensureAuthenticated()
        .then(() => this.billingApi.generateOranizationBill(organizationId, params));

  }

  /**
   * Return an organization bill in PDF or CSV format.
   * @param organizationId Organization ID.
   * @param billId Bill ID.
   * @param format File format: csv or pdf. Default set to pdf.
   * @returns {Promise<ArrayBuffer|string|Object>}
   */
  public getBillContent(organizationId: number, billId: number, format?: string):
    Promise<ArrayBuffer|string|object> {
      if (organizationId < 1 || isNaN(organizationId)) {
        return this.reject(`Organization ID is incorrect [${organizationId}].`);
      }
      if (billId < 1 || isNaN(billId)) {
        return this.reject(`Bill ID is incorrect [${billId}].`);
      }

      return this.authService
        .ensureAuthenticated()
        .then(() => this.billingApi.getBillContent(organizationId, billId, {
          format: format || 'pdf',
        }));
  }

  // #endregion

}
